// services/lesson-summary.service.ts

import axiosInstance from './axios';
import { LessonSummaryDto } from '../types/lesson-summary.types';
import { safeLogger } from '../utils/logger';
import { baseURL } from "../services/axios";

/**
 * Get all lesson summaries for a specific child by ID.
 * Filters results on client side by ChildId.
 */
export const getLessonSummariesByChildId = async (childId: number): Promise<LessonSummaryDto[]> => {
  try {
    const response = await axiosInstance.get(`${baseURL}/Child/${childId}/lessonSummery`);
    console.log(`[API] Summaries for ChildId=${childId}:`, response.data);
    // מיפוי שמות שדות מהשרת לשמות שדות ש-React מצפה להם
    const mapped = response.data.map((summary: any) => ({
      ID: summary.id,
      ChildId: summary.childId,
      Date: summary.date ? new Date(summary.date).toISOString().split('T')[0] : '',
      Title: summary.title,
      LessonContent: summary.lessonContent,
      ExercisesPerformed: summary.exercisesPerformed,
      Conclusions: summary.conclusions,
      NextLessonPlans: summary.nextLessonPlans,
      AccessPermission: summary.accessPermission,
      GeneralizationTasks: summary.generalizationTasks
    }));
    return mapped;
  } catch (error) {
    console.error('Error fetching lesson summaries:', error);
    safeLogger.error('Error fetching lesson summaries');
    throw error;
  }
};

/**
 * Get a single lesson summary by its ID.
 */
export const getLessonSummaryById = async (id: number): Promise<LessonSummaryDto> => {
  try {
    const response = await axiosInstance.get(`${baseURL}/LessonSummary/${id}`);
    return response.data;
  } catch (error) {
    safeLogger.error(`Error fetching lesson summary with ID ${id}`);
    throw error;
  }
};

/**
 * Create a new lesson summary for a child.
 */
export const createLessonSummary = async (summary: LessonSummaryDto): Promise<LessonSummaryDto> => {
  try {
    const { ID, ...summaryData } = summary;

    // Basic validation
    if (!summaryData.Date || !summaryData.Title || !summaryData.LessonContent || !summaryData.ChildId) {
      throw new Error('חסרים שדות חובה בסיכום השיעור');
    }

    const dateObj = new Date(summaryData.Date);
    if (isNaN(dateObj.getTime())) {
      throw new Error('תאריך לא תקין');
    }

    safeLogger.log(`Creating lesson summary for child ${summaryData.ChildId}: ${summaryData.Title}`);
    const response = await axiosInstance.post(`${baseURL}/LessonSummary`, summaryData);
    return response.data;
  } catch (error) {
    safeLogger.error('Error creating lesson summary');
    throw error;
  }
};

/**
 * Update an existing lesson summary.
 */
export const updateLessonSummary = async (id: number, summary: LessonSummaryDto): Promise<void> => {
  try {
    await axiosInstance.put(`${baseURL}/LessonSummary/${id}`, summary);
  } catch (error) {
    safeLogger.error(`Error updating lesson summary with ID ${id}`);
    throw error;
  }
};

/**
 * Delete a lesson summary by ID.
 */
export const deleteLessonSummary = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`${baseURL}/LessonSummary/${id}`);
  } catch (error) {
    safeLogger.error(`Error deleting lesson summary with ID ${id}`);
    throw error;
  }
};
